import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from '~/utils/navigation';
import { insertInitialMetaForFrame, insertTrackEvent } from '@comeon/analytics';
import styled from 'styled-components';
import { BadgeHot } from '@comeon/ui';
import useUIContext from '~/ui/use-ui-context';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { Bigger } from '~/components/layout/mediaquery';
import Portal from '~/components/portal';
import {
	FiltersInner,
	FilterLink,
	StyledExternalLink,
	ProductNavbarFilters as ProductFilters
} from '~/components/header/filters.js';
import {
	ROUTE_USERFEEDBACK,
	ROUTE_PROMOTIONS,
	ROUTE_PROMOTIONS_THUNDERBITE,
	ROUTE_SHOP,
	ROUTE_ACCOUNT_SETTINGS_SELF_EXCLUSION
} from '~/components/router/constants.js';
import { showSupportDialogBox } from '~/components/support/ducks/index.js';
import useTranslations from '~/components/translation/use-translations.ts';
import ProductNavbarLinks from './product-navbar-links.js';
import {
	selectEnableVIPLinkInProductNav,
	selectHasPromotions,
	selectIsShopEnabled,
	selectIsShopExternal
} from '~/components/brand/ducks/index.js';
import MarketplaceLinkItem from './product-navbar-promotion-item/index.js';
import { selectIsThunderbiteTabOpen } from '~/components/promotions/ducks/selectors';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header/index.js';
import { selectProductNavbarShopExternalNewBadgeEnabled } from '~/components/brand/ducks/selectors.js';
import {
	selectIsAboveAgeLimit,
	selectPlayerSelfExcluded,
	selectPlayerBonusBlocked
} from '~/components/account/ducks/index';
import ShopItem from './product-navbar-shop-item.js';
import MarketplacePopup from './product-navbar-marketplace-popup';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';

const PORTAL = 'main-layout';

const ProductNavbarBadgeHot = styled(BadgeHot)`
	position: absolute;
	right: -26px;
	top: -4px;
`;

const TEXT_KEYS = [
	'RESPONSIVE_BLOG_LINK',
	'RESPONSIVE_BLOG_TEXT',
	'RESPONSIVE_SHOP_LINK',
	'MOBILE_GLOBAL_MENU_PROMOTION_PAGE',
	'MOBILE_MAIN_MENU_SHOP',
	'MOBILE_MAIN_MENU_SHOP_EXTERNAL',
	'MOBILE_MAIN_MENU_SUPPORT',
	'FEEDBACK_NAVBAR_LINK_URL',
	'FEEDBACK_NAVBAR_LINK_TITLE',
	'VIP_LINK',
	'VIP_TITLE',
	'SHOP_EXTERNAL_NEW',
	'MARKETPLACE_NL_U24_TITLE',
	'MARKETPLACE_NL_U24_SUBTITLE',
	'MARKETPLACE_NL_U24_GOT_IT',
	'MARKETPLACE_NL_U24_GO_TO',
	'MARKETPLACE_SELF_EXCLUDED_TITLE',
	'MARKETPLACE_SELF_EXCLUDED_SUBTITLE'
];

const ProductNav = () => {
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);
	const authenticated = useSelector(authenticationSelector);
	const isShopExternal = useSelector(selectIsShopExternal);
	const isShopEnabled = useSelector(selectIsShopEnabled);
	const hasPromotions = useSelector(selectHasPromotions);
	const shopExternalNewBadgeEnabled = useSelector(selectProductNavbarShopExternalNewBadgeEnabled);
	const isVIPEnabled = useSelector(selectEnableVIPLinkInProductNav);
	const dispatch = useDispatch();
	const support = useSelector(state => state.support);
	const hasRegulationsHeader = useSelector(selectHasRegulationsHeader);
	const isThunderbiteTabOpen = useSelector(selectIsThunderbiteTabOpen);
	const oldEnough = useSelector(selectIsAboveAgeLimit);
	const isSelfExcluded = useSelector(selectPlayerSelfExcluded);
	const isBonusBlocked = useSelector(selectPlayerBonusBlocked);
	const router = useRouter();
	const transformPath = useContextualPathTransformer();
	const [showModal, setShowModal] = useState(false);
	const { forceAbsoluteSubHeader } = useUIContext();

	return (
		!isLoadingTranslations && (
			<>
				<Bigger>
					<ProductFilters
						hasRegulationsHeader={hasRegulationsHeader}
						authenticated={authenticated}
						data-at="product-nav-bar"
						className={forceAbsoluteSubHeader ? 'u-absolute' : 'u-fixed'}
					>
						<FiltersInner dataAt="left-side-section-product-nav-bar" className="navbar-left">
							<ProductNavbarLinks
								oldEnough={oldEnough}
								isSelfExcluded={isSelfExcluded}
								onClick={() => setShowModal(true)}
							/>
						</FiltersInner>
						<FiltersInner
							alignnavright
							dataAt="right-side-section-product-nav-bar"
							className="navbar-right"
						>
							{translations.RESPONSIVE_BLOG_LINK && (
								<StyledExternalLink
									data-at="blog"
									alignitemright
									target="_blank"
									rel="noopener noreferrer"
									to={translations.RESPONSIVE_BLOG_LINK}
									onClick={() => {
										insertTrackEvent({
											category: 'Top Nav Item',
											action: 'Click',
											label: `blog`
										});
										insertInitialMetaForFrame('blog', dispatch);
									}}
								>
									{translations.RESPONSIVE_BLOG_TEXT}
								</StyledExternalLink>
							)}
							{isVIPEnabled && (
								<StyledExternalLink
									data-at="vip"
									alignitemright
									target="_blank"
									rel="noopener noreferrer"
									to={translations.VIP_LINK}
									onClick={() => {
										insertTrackEvent({
											category: 'Top Nav Item',
											action: 'Click',
											label: `vip`
										});
										insertInitialMetaForFrame('vip', dispatch);
									}}
								>
									{translations.VIP_TITLE}
								</StyledExternalLink>
							)}
							{hasPromotions && oldEnough && (
								<MarketplaceLinkItem
									oldEnough={oldEnough}
									isSelfExcluded={isSelfExcluded}
									href={isThunderbiteTabOpen ? ROUTE_PROMOTIONS_THUNDERBITE : ROUTE_PROMOTIONS}
									label={translations.MOBILE_GLOBAL_MENU_PROMOTION_PAGE}
									onClick={() => setShowModal(true)}
								/>
							)}
							{isShopEnabled && oldEnough && (
								<ShopItem
									label={translations.MOBILE_MAIN_MENU_SHOP}
									data-at="shop"
									href={ROUTE_SHOP}
									onClick={() => setShowModal(true)}
									oldEnough={oldEnough}
									isSelfExcluded={isSelfExcluded}
								/>
							)}
							{support.mail && (
								<FilterLink
									data-at="support"
									alignitemright
									href="/support"
									onClick={() => {
										insertTrackEvent({
											category: 'Top Nav Item',
											action: 'Click',
											label: `support-mail`
										});
										insertInitialMetaForFrame('support', dispatch);
									}}
								>
									{translations.MOBILE_MAIN_MENU_SUPPORT}
								</FilterLink>
							)}
							{/* Dixa Button - From navbar Menu */}
							{support.dixa && (
								<FilterLink
									data-at="dixa"
									to=""
									alignitemright
									onClick={e => {
										e.preventDefault();
										if (window && window.dataLayer) {
											insertTrackEvent({
												category: 'NavBar',
												action: 'DixaButton',
												label: 'Clicked'
											});
											insertTrackEvent({
												category: 'Top Nav Item',
												action: 'Click',
												label: `support-dixa`
											});
											insertInitialMetaForFrame('support', dispatch);
										}
										dispatch(showSupportDialogBox(true));
									}}
								>
									{support.supportLoading && <div className="spinner" />}
									{translations.MOBILE_MAIN_MENU_SUPPORT}
								</FilterLink>
							)}
							{/* Ada Button - From navbar Menu */}
							{support.ada && (
								<FilterLink
									data-at="ada"
									href=""
									alignitemright
									onClick={e => {
										e.preventDefault();
										if (window && window.dataLayer) {
											insertTrackEvent({
												category: 'NavBar',
												action: 'AdaButton',
												label: 'Clicked'
											});
											insertTrackEvent({
												category: 'Top Nav Item',
												action: 'Click',
												label: `support-ada`
											});
											insertInitialMetaForFrame('support', dispatch);
										}
										dispatch(showSupportDialogBox(true));
									}}
								>
									{support.supportLoading && <div className="spinner" />}
									{translations.MOBILE_MAIN_MENU_SUPPORT}
								</FilterLink>
							)}

							{isShopExternal && translations.RESPONSIVE_SHOP_LINK && (
								<StyledExternalLink
									data-at="shop"
									alignitemright
									target="_blank"
									rel="noopener noreferrer"
									to={translations.RESPONSIVE_SHOP_LINK}
									onClick={() => {
										insertTrackEvent({
											category: 'Top Nav Item',
											action: 'Click',
											label: `shop`
										});
										insertInitialMetaForFrame('shop', dispatch);
									}}
								>
									{translations.MOBILE_MAIN_MENU_SHOP_EXTERNAL}
									{shopExternalNewBadgeEnabled && (
										<ProductNavbarBadgeHot>{translations.SHOP_EXTERNAL_NEW}</ProductNavbarBadgeHot>
									)}
								</StyledExternalLink>
							)}
							{translations.FEEDBACK_NAVBAR_LINK_URL && (
								<FilterLink
									data-at="user-feedback"
									alignitemright
									href={ROUTE_USERFEEDBACK}
									onClick={() =>
										insertTrackEvent({
											category: 'Top Nav Item',
											action: 'Click',
											label: `user-feedback`
										})
									}
								>
									{translations.FEEDBACK_NAVBAR_LINK_TITLE}
								</FilterLink>
							)}
						</FiltersInner>
					</ProductFilters>
				</Bigger>
				{showModal ? (
					<Portal node={document.getElementById(PORTAL)}>
						<MarketplacePopup
							translations={translations}
							onConfirm={() => setShowModal(false)}
							onReject={
								oldEnough
									? () => {
											setShowModal(false);
											router.push(transformPath(ROUTE_ACCOUNT_SETTINGS_SELF_EXCLUSION));
										}
									: null
							}
							isSelfExcluded={isSelfExcluded}
							isBonusBlocked={isBonusBlocked}
						/>
					</Portal>
				) : null}
			</>
		)
	);
};

export default ProductNav;
